import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { TourLaunchContext } from '@fordeer/app-tour'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'
import { isHelpLauncherHiddenPath } from '@features/help-launcher'
import { FF_VIRTUAL_TOUR } from '@constant/featureFlags'
import { useThemeEditCodeFeatureFlag } from '@hooks/featureFlags/useFeatureFlags'
import { virtualTourAnalytics } from '@features/virtual-tour/analytics'
import { VirtualTourController } from '@features/virtual-tour/components/VirtualTourController'
import {
  getActiveInvoiceVirtualTours,
  hasInvoiceVirtualTourContentfulConfig,
} from '@features/virtual-tour/contentful/virtualTour.contentful'
import { resolveThemeIdForCustomizationTour } from '@features/virtual-tour/lib/resolveThemeIdForTour'
import { resolveTourSteps } from '@features/virtual-tour/lib/resolveTourSteps'
import { useInvoiceTourStore } from '@features/virtual-tour/store/invoiceTourStore'
import type { InvoiceVirtualTour } from '@features/virtual-tour/types/virtualTour.types'
import {
  CUSTOMIZE_INVOICE_CODE_TOUR_SLUG,
  TOURS_REQUIRING_THEME_ID,
} from '@features/virtual-tour/types/virtualTour.types'
import { postHogErrorTracking } from '../../../services/posthogErrorTracking'

export const useVirtualTourFeature = (): {
  hidden: boolean
  node: ReactNode
  startTourBySlug: (slug: string) => Promise<void>
  tours: InvoiceVirtualTour[]
} => {
  const { pathname } = useLocation()
  const isVirtualTourEnabled = useFeatureFlagEnabled(FF_VIRTUAL_TOUR)
  const showEditCodeTour = useThemeEditCodeFeatureFlag()
  const queryClient = useQueryClient()
  const startTour = useInvoiceTourStore((state) => state.startTour)
  const stopTour = useInvoiceTourStore((state) => state.stopTour)
  const activeStep = useInvoiceTourStore((state) => state.activeStep)
  const runningStepDefinitions = useInvoiceTourStore((state) => state.runningStepDefinitions)
  const [contentfulTours, setContentfulTours] = useState<InvoiceVirtualTour[] | null>(null)
  const [hasContentfulLoadError, setHasContentfulLoadError] = useState(false)
  const isHelpLauncherHidden = isHelpLauncherHiddenPath(pathname)
  const isHidden = !isVirtualTourEnabled || isHelpLauncherHidden
  const isTourControllerActive = activeStep !== null && runningStepDefinitions != null

  useEffect(() => {
    if (isHidden || !hasInvoiceVirtualTourContentfulConfig) {
      setContentfulTours(null)
      setHasContentfulLoadError(false)
      return
    }

    let isMounted = true

    const fetchTours = async () => {
      try {
        const nextTours = await getActiveInvoiceVirtualTours()

        if (!isMounted) {
          return
        }

        setHasContentfulLoadError(false)
        setContentfulTours(nextTours)
      } catch (error) {
        postHogErrorTracking.captureException(error, {
          feature: 'virtual-tour',
          source: 'load-contentful-tours',
          severity: 'error',
        })

        if (isMounted) {
          setHasContentfulLoadError(true)
          setContentfulTours(null)
        }
      }
    }

    fetchTours()

    return () => {
      isMounted = false
    }
  }, [isHidden])

  const tours = useMemo(() => {
    if (isHidden || !hasInvoiceVirtualTourContentfulConfig) {
      return []
    }

    if (hasContentfulLoadError) {
      return []
    }

    const visibleTours = (contentfulTours ?? []).filter(
      (tour) => showEditCodeTour || tour.slug !== CUSTOMIZE_INVOICE_CODE_TOUR_SLUG,
    )

    return visibleTours
  }, [contentfulTours, hasContentfulLoadError, isHidden, showEditCodeTour])

  const startTourBySlug = useCallback(
    async (slug: string) => {
      const tour = tours.find((entry) => entry.slug === slug)

      if (!tour) {
        virtualTourAnalytics.startFailed('missing_tour', { tourSlug: slug })
        postHogErrorTracking.captureException(
          new Error(`Virtual tour "${slug}" is missing`),
          { feature: 'virtual-tour', source: 'start-tour', tourSlug: slug },
        )
        return
      }

      if (!tour.stepDefinitions.length) {
        virtualTourAnalytics.startFailed('no_steps', {
          tourSlug: slug,
          tourLabel: tour.label,
        })
        postHogErrorTracking.captureException(
          new Error(`Virtual tour "${slug}" has no steps`),
          { feature: 'virtual-tour', source: 'start-tour', tourSlug: slug },
        )
        return
      }

      virtualTourAnalytics.startRequested(tour.slug, tour.label)

      const launchContext: TourLaunchContext = {}
      let hasThemeId = false

      if (
        TOURS_REQUIRING_THEME_ID.includes(slug as (typeof TOURS_REQUIRING_THEME_ID)[number])
      ) {
        try {
          const themeId = await resolveThemeIdForCustomizationTour(queryClient)

          if (themeId) {
            launchContext.themeId = themeId
            hasThemeId = true
          }
        } catch (error) {
          virtualTourAnalytics.startFailed('theme_id_unresolved', {
            tourSlug: slug,
            tourLabel: tour.label,
          })
          postHogErrorTracking.captureException(error, {
            feature: 'virtual-tour',
            source: 'resolve-theme-id',
            tourSlug: slug,
          })
          return
        }
      }

      const resolvedSteps = resolveTourSteps(tour.stepDefinitions, launchContext)

      if (!resolvedSteps.length) {
        virtualTourAnalytics.startFailed('no_steps', {
          tourSlug: slug,
          tourLabel: tour.label,
        })
        postHogErrorTracking.captureException(
          new Error(`Virtual tour "${slug}" has no resolvable steps`),
          { feature: 'virtual-tour', source: 'resolve-steps', tourSlug: slug },
        )
        return
      }

      stopTour()
      startTour(tour.stepDefinitions, launchContext)

      virtualTourAnalytics.started({
        tourSlug: tour.slug,
        tourLabel: tour.label,
        tourStepCount: resolvedSteps.length,
        launchId: String(useInvoiceTourStore.getState().launchId),
        contentfulEnabled: hasInvoiceVirtualTourContentfulConfig,
        themeEditCodeFlag: showEditCodeTour,
        hasThemeId,
      })
    },
    [queryClient, showEditCodeTour, startTour, stopTour, tours],
  )

  return {
    hidden: isHidden,
    tours,
    startTourBySlug,
    node:
      !isVirtualTourEnabled || !isTourControllerActive ? null : (
        <VirtualTourController stepDefinitions={runningStepDefinitions} />
      ),
  }
}
